// src/offline/db.js
//
// Archivio locale su IndexedDB.
//
// Non localStorage: ha circa 5 MB, e' sincrono e bloccherebbe l'interfaccia.
// Un rapportino con due firme PNG pesa gia' qualche centinaio di kB, quindi
// una manciata di rapportini in coda lo saturerebbe.
//
// Scritto a mano invece di aggiungere una libreria: servono quattro
// operazioni e il pacchetto e' gia' grosso.

const NOME_DB = 'ciesse-offline';
const VERSIONE = 1;

export const ARCHIVI = {
  OUTBOX: 'outbox',           // rapportini da spedire
  LAVORAZIONI: 'lavorazioni', // lavorazioni assegnate, per lavorare senza rete
  META: 'meta',               // ultima sincronizzazione, codici riservati
};

let connessione = null;

function apri() {
  if (connessione) return connessione;

  connessione = new Promise((risolvi, rifiuta) => {
    if (typeof indexedDB === 'undefined') {
      rifiuta(new Error('IndexedDB non disponibile'));
      return;
    }

    const richiesta = indexedDB.open(NOME_DB, VERSIONE);

    richiesta.onupgradeneeded = () => {
      const db = richiesta.result;

      if (!db.objectStoreNames.contains(ARCHIVI.OUTBOX)) {
        const outbox = db.createObjectStore(ARCHIVI.OUTBOX, { keyPath: 'id' });
        // l'ordine di invio e' quello di creazione: un rapportino
        // compilato prima deve partire prima
        outbox.createIndex('creatoIl', 'creatoIl');
        outbox.createIndex('stato', 'stato');
      }

      if (!db.objectStoreNames.contains(ARCHIVI.LAVORAZIONI)) {
        db.createObjectStore(ARCHIVI.LAVORAZIONI, { keyPath: '_id' });
      }

      if (!db.objectStoreNames.contains(ARCHIVI.META)) {
        db.createObjectStore(ARCHIVI.META, { keyPath: 'chiave' });
      }
    };

    richiesta.onsuccess = () => risolvi(richiesta.result);
    richiesta.onerror = () => rifiuta(richiesta.error);
  });

  // se l'apertura fallisce non si resta bloccati per sempre sul rifiuto
  connessione.catch(() => {
    connessione = null;
  });

  return connessione;
}

function esegui(archivio, modo, operazione) {
  return apri().then(
    (db) =>
      new Promise((risolvi, rifiuta) => {
        const transazione = db.transaction(archivio, modo);
        const richiesta = operazione(transazione.objectStore(archivio));

        transazione.onabort = () => rifiuta(transazione.error);
        transazione.onerror = () => rifiuta(transazione.error);
        transazione.oncomplete = () => risolvi(richiesta ? richiesta.result : undefined);
      })
  );
}

export const scrivi = (archivio, valore) =>
  esegui(archivio, 'readwrite', (s) => s.put(valore));

export const leggi = (archivio, chiave) =>
  esegui(archivio, 'readonly', (s) => s.get(chiave));

export const elimina = (archivio, chiave) =>
  esegui(archivio, 'readwrite', (s) => s.delete(chiave));

export const tutti = (archivio) =>
  esegui(archivio, 'readonly', (s) => s.getAll());

export const conta = (archivio) =>
  esegui(archivio, 'readonly', (s) => s.count());

export const svuota = (archivio) =>
  esegui(archivio, 'readwrite', (s) => s.clear());

/** Sostituisce in blocco il contenuto di un archivio. */
export function sostituisciTutti(archivio, valori) {
  return apri().then(
    (db) =>
      new Promise((risolvi, rifiuta) => {
        const transazione = db.transaction(archivio, 'readwrite');
        const store = transazione.objectStore(archivio);

        store.clear();
        valori.forEach((v) => store.put(v));

        transazione.onabort = () => rifiuta(transazione.error);
        transazione.onerror = () => rifiuta(transazione.error);
        transazione.oncomplete = () => risolvi(valori.length);
      })
  );
}

export const leggiMeta = (chiave) =>
  leggi(ARCHIVI.META, chiave).then((r) => (r ? r.valore : undefined));

export const scriviMeta = (chiave, valore) =>
  scrivi(ARCHIVI.META, { chiave, valore });

/**
 * Pulizia al logout.
 *
 * Svuota i dati scaricati dal server, NON la coda di invio: se il token
 * scade mentre un tecnico ha rapportini ancora da spedire, cancellarli
 * significherebbe buttare via il lavoro di una giornata. Restano li' e
 * partono al rientro.
 *
 * Il rovescio e' che su un dispositivo condiviso i rapportini firmati del
 * tecnico precedente restano nell'archivio locale finche' non vengono
 * spediti. Perdere un verbale firmato e' peggio.
 */
export async function pulisciAlLogout() {
  try {
    await Promise.all([svuota(ARCHIVI.LAVORAZIONI), svuota(ARCHIVI.META)]);
  } catch (err) {
    console.warn('Pulizia archivio locale non riuscita:', err);
  }
}

/** Cancella tutto, coda compresa. Solo su richiesta esplicita. */
export async function cancellaTutto() {
  try {
    await Promise.all(Object.values(ARCHIVI).map((a) => svuota(a)));
  } catch (err) {
    console.warn('Pulizia archivio locale non riuscita:', err);
  }
}

/** Se il browser non lo supporta, chi chiama deve degradare senza rompersi. */
export function disponibile() {
  return typeof indexedDB !== 'undefined';
}
